import { useEffect, useState } from 'react';
import { Stack, Paper, Group, Text, Button, Table, Badge, Loader, Center, Tooltip } from '@mantine/core';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { notifications } from '@mantine/notifications';
import { modals } from '@mantine/modals';
import { IconAdd as IconPlus, IconCustomer } from '../../components/icons/CncIcons';
import { api, imageUrl } from '../../api/client';
import { useAuth, canWrite } from '../../store/auth';
import OrderStatusBar, { type OrderStatus } from './OrderStatusBar';
import NewQuotationModal, { type QuotationDetail } from './NewQuotationModal';
import DeleteOrderSection from './DeleteOrderSection';
import type { OrderPart } from './OrderPartModal';

interface FullOrder {
  id: number;
  orderUid: string;
  customerId: number | null;
  customerName: string | null;
  customerLogo: string | null;
  contactName: string | null;
  technicianName: string | null;
  name: string;
  notes: string | null;
  imagePath: string | null;
  firstPartImagePath: string | null;
  orderDate: string;
  status: OrderStatus;
  shipment: { useDefault: boolean; country: string | null; county: string | null; street: string | null; zip: string | null };
  parts: OrderPart[];
}

interface QuotationSummary {
  id: number;
  quotationUid: string;
  technicianId: number | null;
  technicianName: string | null;
  status: 'DRAFT' | 'SENT' | 'REFUSED' | 'ACCEPTED' | 'EXPORTED';
  sentAt: string | null;
  totalCost: number | null;
  totalPrice: number | null;
  createdAt: string;
  pdfPath: string | null;
}

function quotationMarkupPct(cost: number | null, price: number | null): number | null {
  if (!cost || cost <= 0 || price == null) return null;
  return (price / cost - 1) * 100;
}

function QuotationStatusBadge({ status }: { status: QuotationSummary['status'] }) {
  const color = { DRAFT: 'gray', SENT: 'blue', REFUSED: 'red', ACCEPTED: 'green', EXPORTED: 'violet' }[status];
  return <Badge variant="light" color={color}>{status}</Badge>;
}

export default function InQuotationPage() {
  const { id } = useParams();
  const orderId = Number(id);
  const navigate = useNavigate();
  const { me } = useAuth();
  const writable = canWrite(me?.role);

  const [order, setOrder] = useState<FullOrder | null>(null);
  const [quotations, setQuotations] = useState<QuotationSummary[] | null>(null);
  const [editingQuotationId, setEditingQuotationId] = useState<number | null>(null);

  async function loadAll() {
    const [o, qs] = await Promise.all([
      api.get<FullOrder>(`/orders/${orderId}`),
      api.get<QuotationSummary[]>(`/orders/${orderId}/quotations`),
    ]);
    setOrder(o);
    setQuotations(qs);
  }
  useEffect(() => { loadAll(); }, [orderId]);

  async function copyQuotation(srcId: number) {
    try {
      const q = await api.post<QuotationDetail>(`/orders/${orderId}/quotations?copyFrom=${srcId}`);
      notifications.show({ message: `New quotation ${q.quotationUid} created.`, color: 'green' });
      setEditingQuotationId(q.id);
      await loadAll();
    } catch (e: any) {
      notifications.show({ message: e.message ?? 'Copy failed', color: 'red' });
    }
  }

  function confirmCopy(q: QuotationSummary) {
    modals.openConfirmModal({
      title: `Copy ${q.quotationUid} as a new draft?`,
      children: 'A new quotation will be created with the same parts and processes.',
      labels: { confirm: 'Copy', cancel: 'Cancel' },
      onConfirm: () => copyQuotation(q.id),
    });
  }

  function confirmAccept(q: QuotationSummary) {
    modals.openConfirmModal({
      title: `Mark ${q.quotationUid} as accepted?`,
      children: 'The order will show its totals from this quotation.',
      labels: { confirm: 'Accept', cancel: 'Cancel' },
      confirmProps: { color: 'green' },
      onConfirm: async () => {
        try {
          await api.put(`/orders/${orderId}/quotations/${q.id}`, { status: 'ACCEPTED' });
          notifications.show({ message: 'Quotation accepted.', color: 'green' });
          await loadAll();
        } catch (e: any) {
          notifications.show({ message: e.message ?? 'Accept failed', color: 'red' });
        }
      },
    });
  }

  function confirmRefuse(q: QuotationSummary) {
    modals.openConfirmModal({
      title: `Mark ${q.quotationUid} as refused?`,
      children: 'This sets the quotation status to REFUSED.',
      labels: { confirm: 'Refuse', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await api.put(`/orders/${orderId}/quotations/${q.id}`, { status: 'REFUSED' });
          notifications.show({ message: 'Quotation marked refused.', color: 'gray' });
          await loadAll();
        } catch (e: any) {
          notifications.show({ message: e.message ?? 'Refuse failed', color: 'red' });
        }
      },
    });
  }

  function confirmDelete(q: QuotationSummary) {
    modals.openConfirmModal({
      title: `Delete ${q.quotationUid}?`,
      children: 'This quotation and all its lines will be removed. This cannot be undone.',
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await api.delete(`/orders/${orderId}/quotations/${q.id}`);
          notifications.show({ message: 'Quotation deleted.', color: 'gray' });
          await loadAll();
        } catch (e: any) {
          notifications.show({ message: e.message ?? 'Delete failed', color: 'red' });
        }
      },
    });
  }

  async function createNew() {
    try {
      const q = await api.post<QuotationDetail>(`/orders/${orderId}/quotations`);
      setEditingQuotationId(q.id);
      await loadAll();
    } catch (e: any) {
      notifications.show({ message: e.message ?? 'Create failed', color: 'red' });
    }
  }

  function confirmStartOrder() {
    modals.openConfirmModal({
      title: 'Start this order?',
      children: 'The accepted quotation locks in and the order moves into the Ongoing phase. The Step table starts tracking actual dates from today.',
      labels: { confirm: 'Start Order', cancel: 'Cancel' },
      confirmProps: { color: 'orange' },
      onConfirm: async () => {
        try {
          await api.post(`/orders/${orderId}/start`);
          notifications.show({ message: 'Order started.', color: 'green' });
          // OrderDispatch routes on status, so a refresh lands us on the Ongoing Order Screen.
          navigate(`/orders/${orderId}`);
        } catch (e: any) {
          notifications.show({ message: e.message ?? 'Start failed', color: 'red' });
        }
      },
    });
  }

  if (!order || !quotations) return <Center p="xl"><Loader /></Center>;

  const ship = order.shipment;
  const shipLine = [ship.street, ship.county, ship.zip, ship.country].filter(Boolean).join(', ');
  // Single-accept invariant: at most one ACCEPTED quotation per order. The Start Order CTA
  // only makes sense once that accepted row exists, and Accept must be disabled on every other
  // row to prevent the UI from tempting users into an invalid state the server would 409.
  const acceptedQuotation = quotations.find((q) => q.status === 'ACCEPTED') ?? null;
  const hasAcceptedOther = (q: QuotationSummary) => !!acceptedQuotation && acceptedQuotation.id !== q.id;

  return (
    <Stack>
      <OrderStatusBar current={order.status} />

      <div className="section">
        <Tooltip label="Click to edit in Order Set-up" withArrow openDelay={300}>
          <div
            className="section-header"
            style={{ cursor: 'pointer' }}
            onClick={() => navigate(`/orders/${orderId}/setup`)}
          >
            Order Basics ↗
          </div>
        </Tooltip>
        <div className="section-body" style={{ cursor: 'pointer' }} onClick={() => navigate(`/orders/${orderId}/setup`)}>
          <Group align="flex-start" gap="xl" wrap="wrap">
            <Group gap="sm">
              {order.customerLogo && <img src={imageUrl(order.customerLogo, true)} alt="" style={{ maxHeight: 48 }} />}
              <Stack gap={2}>
                <Group gap={6}>
                  <IconCustomer size={14} />
                  <Text fw={600}>{order.customerName ?? '—'}</Text>
                  {order.firstPartImagePath && (
                    <img
                      src={imageUrl(order.firstPartImagePath, true)}
                      alt="Order"
                      style={{ maxHeight: 36, marginLeft: 6, borderRadius: 3, border: '1px solid rgba(0,0,0,0.1)' }}
                    />
                  )}
                </Group>
                <Text size="xs" c="dimmed">Customer</Text>
              </Stack>
            </Group>
            <Stack gap={2}>
              <Text fw={600}><code>{order.orderUid}</code></Text>
              <Text size="xs" c="dimmed">Order ID</Text>
            </Stack>
            <Stack gap={2}>
              <Text fw={600}>{order.name}</Text>
              <Text size="xs" c="dimmed">Order Name</Text>
            </Stack>
            <Stack gap={2}>
              <Text>{order.contactName ?? '—'}</Text>
              <Text size="xs" c="dimmed">Contact</Text>
            </Stack>
            <Stack gap={2}>
              <Text>{order.technicianName ?? '—'}</Text>
              <Text size="xs" c="dimmed">Technician</Text>
            </Stack>
            <Stack gap={2}>
              <Text>Parts: {order.parts.length}</Text>
              <Text size="xs" c="dimmed">Order Files</Text>
            </Stack>
            <Stack gap={2} style={{ flex: 1, minWidth: 240 }}>
              <Text>{shipLine || '—'}</Text>
              <Text size="xs" c="dimmed">Shipment address</Text>
            </Stack>
          </Group>
        </div>
      </div>

      <div className="section">
        <div className="section-header">Quotes History</div>
        <div className="section-body">
          <Paper withBorder>
            <Table highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ width: 150 }}>Date / Hour</Table.Th>
                  <Table.Th style={{ width: 160 }}>Quotation ID</Table.Th>
                  <Table.Th style={{ width: 110 }}>Cost</Table.Th>
                  <Table.Th style={{ width: 110 }}>Price</Table.Th>
                  <Table.Th style={{ width: 90 }}>Markup</Table.Th>
                  <Table.Th style={{ width: 110 }}>Status</Table.Th>
                  <Table.Th>Technician</Table.Th>
                  <Table.Th style={{ width: 260 }}>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {quotations.map((q) => {
                  const markup = quotationMarkupPct(q.totalCost, q.totalPrice);
                  return (
                    <Table.Tr key={q.id} className="click-row" data-tour="quotation-row" onClick={() => setEditingQuotationId(q.id)}>
                      <Table.Td>{dayjs(q.createdAt).format('YYYY-MM-DD HH:mm')}</Table.Td>
                      <Table.Td><code>{q.quotationUid}</code></Table.Td>
                      <Table.Td>{q.totalCost != null ? `€${q.totalCost.toFixed(2)}` : '—'}</Table.Td>
                      <Table.Td>{q.totalPrice != null ? `€${q.totalPrice.toFixed(2)}` : '—'}</Table.Td>
                      <Table.Td>{markup != null ? `${markup.toFixed(0)}%` : '—'}</Table.Td>
                      <Table.Td><QuotationStatusBadge status={q.status} /></Table.Td>
                      <Table.Td>{q.technicianName ?? '—'}</Table.Td>
                      <Table.Td onClick={(e) => e.stopPropagation()}>
                        <Group gap={4} wrap="nowrap">
                          {q.pdfPath && (
                            <Tooltip label="Download exported PDF" withArrow>
                              <Button
                                size="compact-xs"
                                variant="light"
                                color="violet"
                                component="a"
                                href={`/api/v1/orders/${orderId}/quotations/${q.id}/pdf`}
                                target="_blank"
                              >
                                PDF
                              </Button>
                            </Tooltip>
                          )}
                          <Button size="compact-xs" variant="light" onClick={() => confirmCopy(q)} disabled={!writable}>Copy</Button>
                          <Tooltip
                            label={
                              hasAcceptedOther(q)
                                ? `Another quotation (${acceptedQuotation!.quotationUid}) is already accepted`
                                : q.status !== 'EXPORTED' && q.status !== 'SENT'
                                  ? 'The quotation must be EXPORTED or SENT before it can be accepted'
                                  : ''
                            }
                            disabled={!hasAcceptedOther(q) && (q.status === 'EXPORTED' || q.status === 'SENT')}
                            withArrow
                          >
                            <Button
                              size="compact-xs"
                              variant="light"
                              color="green"
                              data-tour="quotation-accept"
                              onClick={() => confirmAccept(q)}
                              disabled={
                                !writable
                                || q.status === 'ACCEPTED'
                                || (q.status !== 'EXPORTED' && q.status !== 'SENT')
                                || hasAcceptedOther(q)
                              }
                            >Accept</Button>
                          </Tooltip>
                          <Button size="compact-xs" variant="light" color="red" onClick={() => confirmRefuse(q)} disabled={!writable || q.status === 'REFUSED'}>Refuse</Button>
                          <Button size="compact-xs" variant="light" color="red" onClick={() => confirmDelete(q)} disabled={!writable}>Delete</Button>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
                <Table.Tr>
                  <Table.Td colSpan={8}>
                    <Button fullWidth variant="light" leftSection={<IconPlus size={14} />} data-tour="new-quotation" onClick={createNew} disabled={!writable}>
                      Generate new Quotation
                    </Button>
                  </Table.Td>
                </Table.Tr>
              </Table.Tbody>
            </Table>
          </Paper>
        </div>
      </div>

      {acceptedQuotation && (
        <div className="section">
          <div className="section-body">
            <Button
              fullWidth
              size="lg"
              color="orange"
              data-tour="start-order"
              onClick={confirmStartOrder}
              disabled={!writable}
            >
              Start Order — lock in {acceptedQuotation.quotationUid}
            </Button>
            <Text size="xs" c="dimmed" mt="xs" ta="center">
              Moves the order into its Ongoing phase. You can edit actual step dates and flag technical
              difficulties from there.
            </Text>
          </div>
        </div>
      )}

      <DeleteOrderSection orderId={orderId} />

      {editingQuotationId && (
        <NewQuotationModal
          orderId={orderId}
          quotationId={editingQuotationId}
          parts={order.parts}
          onClose={() => { setEditingQuotationId(null); loadAll(); }}
        />
      )}
    </Stack>
  );
}
